import PostsList from 'components/PostsList';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from 'components/ui/card';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CategoryItemDTO, CategoryPageDTO, CategorySummaryDTO } from 'services/api/models/CategoryDTO';
import { PostMetadataDTO } from 'services/api/models/PostDTO';
import { PosthogOnArticleOpenExtras } from 'utils/ContentUtils';
import { CategoryIconBox } from './CategoryIcon';
import CategoryRows from './CategoryRows';
import { openCategory } from './navigation';

type CategoryItem = Extract<CategoryItemDTO, { type: 'category' }>;

/** Consecutive posts and flat themes read as one list, each row labelled with its theme */
type Group =
  | { kind: 'posts'; posts: PostMetadataDTO[]; labels: Map<number, string> }
  | { kind: 'block'; item: CategoryItem }
  | { kind: 'card'; item: CategoryItem };

function groupItems(items: CategoryItemDTO[]): { groups: Group[]; also: CategoryItem[]; links: CategorySummaryDTO[] } {
  const groups: Group[] = [];
  const links: CategorySummaryDTO[] = [];
  const sections = items.filter(item => item.type === 'category' && item.presentation === 'section') as CategoryItem[];
  // Single-article sections would make short, meaningless headings: they are
  // gathered under "Et aussi" once the page has a few real sections.
  const alsoIds = new Set(
    sections.length >= 3 ? sections.filter(s => s.posts.length === 1).map(s => s.category.id) : [],
  );
  const also: CategoryItem[] = [];

  const pushPosts = (posts: PostMetadataDTO[], label?: string) => {
    const last = groups[groups.length - 1];
    const group =
      last?.kind === 'posts' ? last : { kind: 'posts' as const, posts: [], labels: new Map<number, string>() };
    if (group !== last) groups.push(group);
    posts.forEach(post => {
      group.posts.push(post);
      if (label) group.labels.set(post.id, label);
    });
  };

  items.forEach(item => {
    if (item.type === 'post') {
      pushPosts(item.posts);
    } else if (item.presentation === 'flat') {
      pushPosts(item.posts, item.category.name);
    } else if (item.presentation === 'link') {
      links.push(item.category);
    } else if (item.presentation === 'card') {
      groups.push({ kind: 'card', item });
    } else if (alsoIds.has(item.category.id)) {
      also.push(item);
    } else {
      groups.push({ kind: 'block', item });
    }
  });
  return { groups, also, links };
}

function Block({
  title,
  count,
  posts,
  labels,
  extras,
  testIdPrefix,
}: {
  title?: string;
  count?: number;
  posts: PostMetadataDTO[];
  labels?: Map<number, string>;
  extras: PosthogOnArticleOpenExtras;
  testIdPrefix: string;
}) {
  return (
    <section className="flex flex-col gap-4" data-testid={testIdPrefix}>
      {title && (
        <h4 className="flex items-baseline justify-between border-b pb-2">
          {title}
          {count !== undefined && <small className="text-sm font-normal text-muted-foreground">{count}</small>}
        </h4>
      )}
      <PostsList
        posts={posts}
        getLabel={labels ? post => labels.get(post.id) : undefined}
        posthogOnArticleOpenExtras={extras}
        testIdPrefix={testIdPrefix}
      />
    </section>
  );
}

/**
 * A theme with a page of its own: a framed panel, clearly one object the
 * button at its bottom opens, unlike the plain blocks around it.
 */
function ThemeCard({ item, extras }: { item: CategoryItem; extras: PosthogOnArticleOpenExtras }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { category, posts } = item;

  return (
    <Card
      // The rows inside are white cards themselves: on a white card they would vanish
      className="bg-muted"
      data-testid={`card-${category.slug}`}
    >
      <CardHeader className="flex flex-row items-center gap-2.5">
        <CategoryIconBox category={category} size="sm" />
        <CardTitle asChild className="min-w-0 flex-1">
          <h4>{category.name}</h4>
        </CardTitle>
        <CardDescription className="whitespace-nowrap">
          {t('infoPage.structure.articles', { count: category.post_count })}
          {category.read_count > 0 && ` · ${t('infoPage.structure.read', { count: category.read_count })}`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <PostsList posts={posts} posthogOnArticleOpenExtras={extras} testIdPrefix={`card-${category.slug}-post`} />
      </CardContent>
      <CardFooter>
        <Button
          variant="outline"
          size="lg"
          className="w-full"
          onClick={() => openCategory(navigate, category.slug, 'info-card')}
        >
          {t('infoPage.structure.seeAllArticles', { count: category.post_count })}
        </Button>
      </CardFooter>
    </Card>
  );
}

/**
 * The body of a category page: the categories and posts it contains, each
 * shown the way the backend decided from its size (`presentation`).
 */
export default function CategoryPageItems({
  page,
  selectedId,
}: {
  page: CategoryPageDTO;
  /** The chip selected on the page: show only that category; none selected shows everything */
  selectedId?: number | null;
}) {
  const { t } = useTranslation();
  const items =
    selectedId != null
      ? page.items.filter(item => item.type === 'category' && item.category.id === selectedId)
      : page.items;
  const { groups, also, links } = groupItems(items);
  const extras: PosthogOnArticleOpenExtras = { opened_on_page: 'info-category', found_using_category: page.slug };

  return (
    <div className="flex flex-col gap-9">
      {groups.map((group, index) => {
        if (group.kind === 'posts') {
          return (
            <Block
              key={`posts-${index}`}
              posts={group.posts}
              labels={group.labels}
              extras={extras}
              testIdPrefix={`posts-${index}`}
            />
          );
        }
        if (group.kind === 'card') {
          return <ThemeCard key={group.item.category.id} item={group.item} extras={extras} />;
        }
        const { category, posts } = group.item;
        return (
          <Block
            key={category.id}
            title={category.name}
            count={category.show_count || category.kind === 'section' ? posts.length : undefined}
            posts={posts}
            extras={extras}
            testIdPrefix={`block-${category.slug}`}
          />
        );
      })}
      {also.length > 0 && (
        <Block
          title={t('infoPage.structure.also')}
          posts={also.flatMap(item => item.posts)}
          labels={
            new Map(also.flatMap(item => item.posts.map(post => [post.id, item.category.name] as [number, string])))
          }
          extras={extras}
          testIdPrefix="block-also"
        />
      )}
      {links.length > 0 && (
        <section className="flex flex-col gap-3">
          <h4>{t('infoPage.structure.seeAlso')}</h4>
          <CategoryRows categories={links} openedFrom="info-see-also" testIdPrefix="see-also" />
        </section>
      )}
    </div>
  );
}
